/**
 * features/quotes/QuoteEditor.tsx
 * Create/edit a draft quote, or view a sent one (read-only) with
 * Accept / Decline / Convert-to-invoice and PDF / Email actions.
 */
import { useMemo, useState } from 'react';
import { X, Plus, Trash2, FileSignature, Download, Mail, Check, Ban, ArrowRightCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useSettings } from '@/contexts/SettingsContext';
import { useClients } from '@/features/clients/useClients';
import { buildLineItems, computeTotals, formatPula } from '@/lib/money';
import { downloadQuotePdf, quotePdfBase64, quoteFileName } from '@/lib/quotePdf';
import { glassSurface } from '@/components/ui/glass';
import { Button, Field, Input, Select, Textarea } from '@/components/ui/primitives';
import { SendDocumentModal } from '@/features/common/SendDocumentModal';
import { DEFAULT_COMPANY_SETTINGS, type Quote, type VatTreatment } from '@/types';
import {
  clientSnapshot,
  saveDraft,
  sendQuote,
  setQuoteStatus,
  deleteDraft,
  convertToInvoice,
  type QuotePayload,
} from './quotesWrite';

interface Row {
  id: string;
  description: string;
  quantity: string;
  unitPrice: string;
  vatTreatment: VatTreatment;
}
function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function plusDays(base: string, days: number) {
  const d = new Date(base);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}
function newRow(): Row {
  return { id: crypto.randomUUID(), description: '', quantity: '1', unitPrice: '', vatTreatment: 'standard' };
}

export function QuoteEditor({ quote, onClose }: { quote: Quote | null; onClose: () => void }) {
  const { user } = useAuth();
  const { currentVatSnapshot, settings } = useSettings();
  const { clients } = useClients();
  const vat = currentVatSnapshot();
  const company = settings ?? DEFAULT_COMPANY_SETTINGS;

  const readOnly = quote != null && quote.status !== 'draft';
  const issuer = company.tradingName || company.legalName;

  const [clientId, setClientId] = useState(quote?.clientId ?? '');
  const [issueDate, setIssueDate] = useState(quote?.issueDate ?? todayISO());
  const [validUntil, setValidUntil] = useState(quote?.validUntil ?? plusDays(todayISO(), 30));
  const [notes, setNotes] = useState(quote?.notes ?? '');
  const [terms, setTerms] = useState(quote?.terms ?? '');
  const [rows, setRows] = useState<Row[]>(
    quote
      ? quote.lineItems.map((l) => ({
          id: l.id,
          description: l.description,
          quantity: String(l.quantity),
          unitPrice: String(l.unitPrice),
          vatTreatment: l.vatTreatment,
        }))
      : [newRow()],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [showSend, setShowSend] = useState(false);

  const built = useMemo(
    () =>
      buildLineItems(
        rows.map((r) => ({
          id: r.id,
          description: r.description.trim(),
          quantity: Number(r.quantity) || 0,
          unitPrice: Number(r.unitPrice) || 0,
          vatTreatment: r.vatTreatment,
        })),
        vat,
      ),
    [rows, vat],
  );
  const totals = useMemo(() => computeTotals(built), [built]);
  const activeClients = clients.filter((c) => c.active || c.id === clientId);
  const selectedClient = clients.find((c) => c.id === clientId);
  const valid = clientId.trim().length > 0 && built.some((l) => l.description.length > 0 && l.quantity > 0);
  const actor = user?.email ?? user?.uid ?? 'unknown';

  function setRow(i: number, key: keyof Row, value: string) {
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [key]: value } : r)));
  }
  function buildPayload(): QuotePayload | null {
    if (!selectedClient && !quote?.client) return null;
    const client = selectedClient ? clientSnapshot(selectedClient) : quote!.client;
    const lines = built.filter((l) => l.description.length > 0);
    return {
      clientId,
      client,
      issueDate,
      validUntil,
      lineItems: lines,
      totals: computeTotals(lines),
      vat,
      ...(notes.trim() ? { notes: notes.trim() } : {}),
      ...(terms.trim() ? { terms: terms.trim() } : {}),
    };
  }

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    setError('');
    try {
      await fn();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong.');
      setBusy(false);
    }
  }

  function doSaveDraft() {
    const p = buildPayload();
    if (!p) return setError('Choose a client.');
    void run(() => saveDraft(p, actor, quote?.id));
  }
  function doSend() {
    const p = buildPayload();
    if (!p) return setError('Choose a client.');
    if (!window.confirm('Send this quote? It will be assigned a permanent number.')) return;
    void run(() => sendQuote(p, actor, quote?.id));
  }
  function doConvert() {
    if (!quote) return;
    if (!window.confirm('Convert this quote into a draft invoice? You can review and issue it from Invoices.')) return;
    void run(() => convertToInvoice(quote, actor));
  }

  const title = quote ? (readOnly ? `Quote ${quote.number}` : 'Edit draft quote') : 'New quote';

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 backdrop-blur-md">
      <div className="my-8 w-full max-w-3xl">
        <div className={glassSurface('p-5 space-y-4')}>
          <div className="-mx-5 -mt-5 mb-1 flex items-center justify-between rounded-t-card bg-brand px-5 py-3.5">
            <div className="flex items-center gap-2 text-white">
              <FileSignature size={18} />
              <h2 className="text-base font-semibold text-white">{title}</h2>
              {quote && (
                <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs font-semibold text-white">
                  {quote.status}
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="rounded-lg p-1 text-white/80 transition hover:bg-white/15 hover:text-white"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Client">
              {readOnly ? (
                <Input value={quote!.client.name} disabled />
              ) : (
                <Select value={clientId} onChange={(e) => setClientId(e.target.value)}>
                  <option value="">Select a client…</option>
                  {activeClients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Date">
              <Input type="date" value={issueDate} disabled={readOnly} onChange={(e) => setIssueDate(e.target.value)} />
            </Field>
            <Field label="Valid until">
              <Input type="date" value={validUntil} disabled={readOnly} onChange={(e) => setValidUntil(e.target.value)} />
            </Field>
          </div>

          {/* Line items */}
          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wide text-primaryLight dark:text-gray-400">
                Line items
              </span>
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => setRows((rs) => [...rs, newRow()])}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-brand hover:underline"
                >
                  <Plus size={13} /> Add line
                </button>
              )}
            </div>
            <div className="space-y-2">
              {rows.map((r, i) => {
                const line = built[i];
                return (
                  <div
                    key={r.id}
                    className="grid grid-cols-[1fr_64px_96px_auto] items-center gap-2 rounded-xl border border-black/5 p-2 dark:border-white/10"
                  >
                    <Input placeholder="Description" value={r.description} disabled={readOnly} onChange={(e) => setRow(i, 'description', e.target.value)} />
                    <Input inputMode="decimal" placeholder="Qty" value={r.quantity} disabled={readOnly} onChange={(e) => setRow(i, 'quantity', e.target.value)} className="text-right" />
                    <Input inputMode="decimal" placeholder="Unit price" value={r.unitPrice} disabled={readOnly} onChange={(e) => setRow(i, 'unitPrice', e.target.value)} className="text-right" />
                    <div className="flex items-center gap-2">
                      <span className="w-24 text-right text-sm font-medium text-primary dark:text-gray-200">
                        {formatPula(line?.lineTotal ?? 0)}
                      </span>
                      {!readOnly && rows.length > 1 && (
                        <button
                          type="button"
                          onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}
                          className="rounded-lg p-1.5 text-gray-400 hover:bg-black/5 hover:text-red-600 dark:hover:bg-white/5"
                          aria-label="Remove line"
                        >
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                    {vat.registered && !readOnly && (
                      <div className="col-span-4">
                        <Select value={r.vatTreatment} onChange={(e) => setRow(i, 'vatTreatment', e.target.value)} className="text-xs">
                          <option value="standard">Standard ({vat.rate}% VAT)</option>
                          <option value="zero">Zero-rated</option>
                          <option value="exempt">Exempt</option>
                        </Select>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Totals */}
          <div className="flex justify-end">
            <div className="w-64 space-y-1 text-sm">
              <div className="flex justify-between text-primaryLight dark:text-gray-400">
                <span>Subtotal</span>
                <span>{formatPula(totals.subtotal)}</span>
              </div>
              {vat.registered && (
                <div className="flex justify-between text-primaryLight dark:text-gray-400">
                  <span>VAT ({vat.rate}%)</span>
                  <span>{formatPula(totals.vatTotal)}</span>
                </div>
              )}
              <div className="flex justify-between border-t border-black/10 pt-1 text-base font-bold text-primary dark:border-white/10 dark:text-gray-100">
                <span>Total</span>
                <span>{formatPula(totals.total)}</span>
              </div>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Notes">
              {readOnly ? <Textarea value={quote!.notes ?? ''} rows={2} disabled /> : <Textarea value={notes} rows={2} onChange={(e) => setNotes(e.target.value)} />}
            </Field>
            <Field label="Terms">
              {readOnly ? <Textarea value={quote!.terms ?? ''} rows={2} disabled /> : <Textarea value={terms} rows={2} onChange={(e) => setTerms(e.target.value)} />}
            </Field>
          </div>

          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

          {/* Footer */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex gap-2">
              {!readOnly && quote && (
                <Button variant="ghost" onClick={() => void run(() => deleteDraft(quote.id))} disabled={busy}>
                  Discard
                </Button>
              )}
              {quote && quote.status === 'sent' && (
                <>
                  <Button variant="secondary" icon={<Check size={16} />} onClick={() => void run(() => setQuoteStatus(quote.id, 'accepted'))} loading={busy}>
                    Accept
                  </Button>
                  <Button variant="danger" icon={<Ban size={16} />} onClick={() => void run(() => setQuoteStatus(quote.id, 'declined'))} loading={busy}>
                    Decline
                  </Button>
                </>
              )}
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              {readOnly && quote && (
                <>
                  <Button variant="secondary" icon={<Download size={16} />} onClick={() => downloadQuotePdf(quote, company)}>
                    PDF
                  </Button>
                  <Button variant="secondary" icon={<Mail size={16} />} onClick={() => setShowSend(true)}>
                    Email
                  </Button>
                  {(quote.status === 'sent' || quote.status === 'accepted') && (
                    <Button icon={<ArrowRightCircle size={16} />} onClick={doConvert} loading={busy}>
                      Convert to invoice
                    </Button>
                  )}
                </>
              )}
              <Button variant="secondary" onClick={onClose}>
                {readOnly ? 'Close' : 'Cancel'}
              </Button>
              {!readOnly && (
                <>
                  <Button variant="secondary" onClick={doSaveDraft} loading={busy} disabled={!valid}>
                    Save draft
                  </Button>
                  <Button onClick={doSend} loading={busy} disabled={!valid}>
                    Send quote
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {showSend && quote && (
        <SendDocumentModal
          heading={`Email quote ${quote.number}`}
          to={quote.client.email ?? ''}
          subject={`Quotation ${quote.number} from ${issuer}`}
          message={`Dear ${quote.client.name},\n\nPlease find attached quotation ${quote.number} for ${formatPula(
            quote.totals.total,
          )}, valid until ${new Date(quote.validUntil).toLocaleDateString('en-BW')}.\n\nKind regards,\n${issuer}`}
          filename={quoteFileName(quote)}
          contentBase64={quotePdfBase64(quote, company)}
          relatedEntityType="quote"
          relatedEntityId={quote.id}
          onClose={() => setShowSend(false)}
        />
      )}
    </div>
  );
}
